import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUpdateTask } from '@/hooks/useTasks';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';

interface TaskActionsProps {
  taskId: string;
}

export default function TaskActions({ taskId }: TaskActionsProps) {
  const [confirmAction, setConfirmAction] = useState<'completed' | 'ignored' | null>(null);
  const [userNotes, setUserNotes] = useState('');
  const updateTask = useUpdateTask();
  const navigate = useNavigate();

  const handleConfirm = async () => {
    if (!confirmAction) return;

    try {
      await updateTask.mutateAsync({
        taskId,
        data: {
          status: confirmAction,
          user_notes: userNotes.trim() || undefined,
        },
      });
      setConfirmAction(null);
      setUserNotes('');
      navigate('/app/tasks');
    } catch {
      // Error handled by mutation
    }
  };

  const handleClose = () => {
    setConfirmAction(null);
    setUserNotes('');
  };

  return (
    <>
      <div className="flex items-center justify-center gap-4 border-t border-neutral-200 pt-6">
        <Button
          variant="primary"
          size="lg"
          onClick={() => setConfirmAction('completed')}
          className="bg-success-500 hover:bg-success-600 focus:ring-success-500"
        >
          ✓ Done
        </Button>
        <Button
          variant="secondary"
          size="lg"
          onClick={() => setConfirmAction('ignored')}
        >
          Ignore
        </Button>
      </div>

      {/* Feedback modal */}
      <Modal
        isOpen={!!confirmAction}
        onClose={handleClose}
        title={confirmAction === 'completed' ? 'Any notes on how it went?' : 'Any notes on why you\'re skipping this?'}
        size="sm"
      >
        <textarea
          value={userNotes}
          onChange={(e) => setUserNotes(e.target.value)}
          placeholder="Optional"
          rows={5}
          maxLength={2000}
          className="mb-4 w-full resize-none overflow-y-auto rounded-lg border border-neutral-300 p-3 text-sm text-neutral-300 placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            loading={updateTask.isPending}
            className={confirmAction === 'completed' ? 'bg-success-500 hover:bg-success-600' : ''}
          >
            {confirmAction === 'completed' ? 'Mark Done' : 'Ignore'}
          </Button>
        </div>
      </Modal>
    </>
  );
}
